import React, { useEffect, useRef, useState } from "react"
import { HeaderMenuFieldsFragment } from "../../../../../graphql-types"
import * as styles from "./dropdown-menu-bar.module.scss"

type Props = {
  headerMenu: HeaderMenuFieldsFragment
  locale: string
  defaultLocale: string
}

export const KEY_CODES = Object.freeze({
  TAB: 9,
  RETURN: 13,
  ESC: 27,
  SPACE: 32,
  END: 35,
  HOME: 36,
  UP: 38,
  DOWN: 40,
  LEFT: 37,
  RIGHT: 39,
})

type SubMenuType = NonNullable<NonNullable<HeaderMenuFieldsFragment["menuSection"]>[number]>["subMenu"]

interface DisplayedMenu {
  label: string
  link: string | null
  subMenu: SubMenuType
  open: boolean
  target: string
}

const extractDisplayedMenus = (
  selectedHeaderMenu: HeaderMenuFieldsFragment,
  locale: string,
  defaultLocale: string
): DisplayedMenu[] =>
  (selectedHeaderMenu.menuSection || [])
    .filter(menuItem => menuItem?.headerDisplay)
    .map(menuItem => ({
      label: menuItem?.label,
      link: menuItem?.page
        ? locale === defaultLocale
          ? `/${menuItem.page.slug}`
          : `/${locale}/${menuItem.page.slug}`
        : menuItem?.externalPage,
      subMenu: menuItem?.subMenu,
      open: false,
      target: menuItem?.page ? "_self" : menuItem?.externalPage ? "_blank" : null,
    }))

const DropdownNavbar: React.FC<Props> = ({ headerMenu, locale, defaultLocale }) => {
  const [menus, setMenus] = useState<DisplayedMenu[]>([])
  const disclosureButtons = useRef<Array<HTMLButtonElement | null>>([])

  // Extract menu displayed in header
  useEffect(() => {
    setMenus(extractDisplayedMenus(headerMenu, locale, defaultLocale))
  }, [headerMenu, locale, defaultLocale])

  // Close menu on ESC
  useEffect(() => {
    if (typeof window === "undefined") {
      return
    }

    const onKeyDown = event => {
      if (event.key === "Escape") {
        event.preventDefault()
        event.stopPropagation()
        const openMenuIndex = menus.findIndex(menu => menu.open)
        closeAllMenus()
        disclosureButtons.current[openMenuIndex]?.focus()
        return
      }
      return
    }

    window.addEventListener("keydown", onKeyDown)
    return () => window.removeEventListener("keydown", onKeyDown)
  }, [menus])

  function openMenu(menuIndex: number) {
    setMenus(currentMenus =>
      currentMenus.map((menu, index) => {
        return { ...menu, open: index === menuIndex }
      })
    )
  }

  function closeAllMenus() {
    setMenus(currentMenus => currentMenus.map(menu => ({ ...menu, open: false })))
  }

  function toggleMenu(menuIndex: number) {
    setMenus(currentMenus =>
      currentMenus.map((menu, index) => ({ ...menu, open: index === menuIndex ? !menu.open : false }))
    )
  }

  function buildDropdownButton(displayedMenu: DisplayedMenu, menuIndex: number) {
    const parsedSubMenu = displayedMenu.subMenu?.filter(item => {
      return item.headerDisplay && ((item.page && item.page.slug) || item.externalPage)
    })

    const hasSubMenu = Boolean(parsedSubMenu?.length)
    const submenuId = `menu-${menuIndex}`
    const disclosureId = `dropdownButton-${menuIndex}`

    return (
      <li
        className={styles.dropdownMenu}
        key={displayedMenu.link || displayedMenu.label || menuIndex}
        onMouseEnter={() => hasSubMenu && openMenu(menuIndex)}
        onBlur={event => {
          if (!event.currentTarget.contains(event.relatedTarget)) {
            closeAllMenus()
          }
        }}
      >
        {hasSubMenu ? (
          <button
            ref={button => {
              disclosureButtons.current[menuIndex] = button
            }}
            id={disclosureId}
            type="button"
            className={`${styles.dropdownMenuLabel} ${styles.disclosureButton}`}
            aria-controls={submenuId}
            aria-expanded={displayedMenu.open}
            onClick={() => toggleMenu(menuIndex)}
            onKeyDown={event => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault()
                toggleMenu(menuIndex)
                return
              }
              if (event.key === "ArrowDown") {
                event.preventDefault()
                openMenu(menuIndex)
                requestAnimationFrame(() => document.querySelector<HTMLAnchorElement>(`#${submenuId} a`)?.focus())
              }
            }}
          >
            {displayedMenu.label}
            <span className={styles.disclosureIcon} aria-hidden="true" />
          </button>
        ) : (
          <a className={styles.dropdownMenuLabel} href={displayedMenu.link} target={displayedMenu.target}>
            {displayedMenu.label}
          </a>
        )}
        {hasSubMenu && (
          <div
            hidden={!displayedMenu.open}
            className={
              displayedMenu.open ? `${styles.dropdownMenuContent} ${styles.active}` : styles.dropdownMenuContent
            }
          >
            <ul
              id={submenuId}
              className={styles.subMenuItemList}
              aria-labelledby={disclosureId}
              hidden={!displayedMenu.open}
            >
              {displayedMenu.link && (
                <li key={`${submenuId}-parent`} className={styles.subMenuItem}>
                  <a href={displayedMenu.link} target={displayedMenu.target}>
                    {displayedMenu.label}
                  </a>
                </li>
              )}
              {parsedSubMenu.map((subMenu, index) => buildDropdownSubmenuItem(subMenu, index))}
            </ul>
          </div>
        )}
      </li>
    )
  }

  function buildDropdownSubmenuItem(subMenuItem, index: number) {
    if (!subMenuItem.headerDisplay) {
      return null
    }

    const href = subMenuItem.page
      ? locale === defaultLocale
        ? `/${subMenuItem.page.slug}`
        : `/${locale}/${subMenuItem.page.slug}`
      : subMenuItem.externalPage

    return (
      <li key={href || subMenuItem.label || index} className={styles.subMenuItem}>
        <a href={href} target={subMenuItem.page ? "_self" : subMenuItem.externalPage ? "_blank" : null}>
          {subMenuItem.label}
        </a>
      </li>
    )
  }

  return (
    <nav className={styles.navbar} aria-label={headerMenu.mainNavigationAriaLabel} onMouseLeave={closeAllMenus}>
      <ul id="dropDownMenu" className={styles.navbarDropdownMenuList}>
        {menus.map((displayedMenu, menuIndex) => buildDropdownButton(displayedMenu, menuIndex))}
      </ul>
    </nav>
  )
}

export default DropdownNavbar
